export interface FeatureBasedTranslations {
  [key: string]:
    | any
    | {
        en?: string
        fr?: string
        de?: string
        it?: string
      }
}

export interface StandardResources {
  en: Record<string, any>
  fr: Record<string, any>
  de: Record<string, any>
  it: Record<string, any>
}

/**
 * Transforms feature-based translation structure to i18next standard format
 * From: { key: { en: "English", fr: "French" } }
 * To: { en: { key: "English" }, fr: { key: "French" } }
 */
export function transformFeatureToLanguage(
  featureData: FeatureBasedTranslations
): StandardResources {
  const languages = ['en', 'fr', 'de', 'it']
  const result: StandardResources = {
    en: {},
    fr: {},
    de: {},
    it: {},
  }

  function processObject(obj: any, path: string[] = []): void {
    Object.entries(obj).forEach(([key, value]) => {
      const currentPath = [...path, key]

      // Check if this is a translation object (has language keys)
      if (
        typeof value === 'object' &&
        value !== null &&
        languages.some((lang) => lang in value) &&
        Object.keys(value).every((k) => languages.includes(k))
      ) {
        // This is a translation leaf node
        const fullKey = currentPath.join('.')

        languages.forEach((lang) => {
          const translationValue = (value as Record<string, string>)[lang]
          if (translationValue !== undefined) {
            setNestedValue(
              result[lang as keyof StandardResources],
              currentPath,
              translationValue
            )
          }
        })
      } else if (typeof value === 'object' && value !== null) {
        // Continue recursing
        processObject(value, currentPath)
      }
    })
  }

  function setNestedValue(obj: any, path: string[], value: string): void {
    const lastKey = path[path.length - 1]
    const parentPath = path.slice(0, -1)

    let current = obj
    parentPath.forEach((key) => {
      if (!current[key]) {
        current[key] = {}
      }
      current = current[key]
    })

    current[lastKey] = value
  }

  processObject(featureData)
  return result
}

/**
 * Load all feature-based translation files and transform them
 */
export function loadFeatureBasedTranslations(
  featureModules: Record<string, FeatureBasedTranslations>
): StandardResources {
  const combinedResources: StandardResources = {
    en: {},
    fr: {},
    de: {},
    it: {},
  }

  Object.entries(featureModules).forEach(([namespace, featureData]) => {
    const transformed = transformFeatureToLanguage(featureData)

    // Merge into combined resources
    Object.entries(transformed).forEach(([lang, data]) => {
      combinedResources[lang as keyof StandardResources][namespace] = data
    })
  })

  return combinedResources
}
